import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, TextField, MenuItem, Button, Alert, CircularProgress,
  Checkbox, FormControlLabel, Paper, Divider, Stack, Autocomplete,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination, IconButton, Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import axios from "../../../config/axiosConfig";
import EditarEgresoDialog from "./EditarEgresoDialog";
import { CAJA_CHICA_COLOR } from "../theme";
import { useAuth } from "../../../context/AuthContext";
import { esRolAdminGerenteContable } from "../permisos";
import { fieldSx as compactFieldSx } from "../../egresos/components/formStyles";

// admin/gerente/contable pueden editar cualquier Egreso. El resto solo los del último mes: el backend marca cada fila con `en_ultimo_mes` (misma condición que valida al
// guardar), así que no hay que recalcular fechas acá.
const puedeEditarFila = (user, egreso) => esRolAdminGerenteContable(user) || Boolean(Number(egreso.en_ultimo_mes));

const Th = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.74rem",
  padding: "2px 6px",
  lineHeight: 1.2,
  color: theme.palette.mode === "dark" ? theme.palette.common.white : CAJA_CHICA_COLOR,
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : `${CAJA_CHICA_COLOR}14`,
}));

// Mismo criterio de compactado que ya usan otros formularios de la app (campos más
// bajos y con menos padding, sin cambiar el ancho — eso lo sigue definiendo el grid).
// Multilínea compacto (igual que la observación del formulario de Egresos).

const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const hoyISO = () => new Date().toISOString().slice(0, 10);

const initialForm = {
  fecha: hoyISO(), item: "", detalle: "", num_fac: "", num_rec: "", monto: "", servicio: "General",
  conRetencion: false, tipo_retencion: "",
  fecha_factura: hoyISO(), nit: "", denominacion: "", nro_autorizacion: "", cod_control: "",
};

const RegistroEgresosTab = () => {
  const { user } = useAuth();

  const [saldo, setSaldo] = useState(null);
  const [egresos, setEgresos] = useState([]);
  const [totalEgresos, setTotalEgresos] = useState(0);
  const [pagina, setPagina] = useState(0); // 0-based, como espera TablePagination
  const [filasPorPagina, setFilasPorPagina] = useState(10);
  const [loadingLista, setLoadingLista] = useState(true);
  const [errorLista, setErrorLista] = useState("");

  const [items, setItems] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [retenciones, setRetenciones] = useState([]);

  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [buscandoNit, setBuscandoNit] = useState(false);
  const [nitInput, setNitInput] = useState("");
  const [opcionesNit, setOpcionesNit] = useState([]);

  const [editarId, setEditarId] = useState(null);

  const cargarCatalogos = useCallback(() => {
    Promise.all([
      axios.get("/api/caja-chica/items"),
      axios.get("/api/caja-chica/servicios"),
      axios.get("/api/caja-chica/retenciones"),
    ]).then(([resItems, resServicios, resRetenciones]) => {
      setItems(resItems.data?.data || []);
      setServicios(resServicios.data?.data || []);
      setRetenciones(resRetenciones.data?.data || []);
    }).catch(() => {});
  }, []);

  const cargarLista = useCallback((paginaObjetivo = pagina, filasObjetivo = filasPorPagina) => {
    setLoadingLista(true);
    setErrorLista("");
    Promise.all([
      axios.get("/api/caja-chica/egresos/saldo"),
      axios.get("/api/caja-chica/egresos", { params: { page: paginaObjetivo + 1, pageSize: filasObjetivo } }),
    ]).then(([resSaldo, resEgresos]) => {
      setSaldo(resSaldo.data?.data?.monto ?? null);
      setEgresos(resEgresos.data?.data?.items || []);
      setTotalEgresos(Number(resEgresos.data?.data?.total || 0));
    }).catch((e) => setErrorLista(e?.response?.data?.message || "Error al cargar los egresos."))
      .finally(() => setLoadingLista(false));
  }, [pagina, filasPorPagina]);

  useEffect(() => {
    cargarCatalogos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    cargarLista(pagina, filasPorPagina);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina, filasPorPagina]);

  const handleChange = (campo) => (e) => setForm((prev) => ({ ...prev, [campo]: e.target.value }));

  // Mismo comportamiento mutuamente excluyente del legacy: cargar N° Factura oculta
  // N° Recibo (y muestra el bloque de facturación); cargar N° Recibo oculta N° Factura.
  const handleNumFac = (e) => setForm((prev) => ({ ...prev, num_fac: e.target.value, num_rec: e.target.value ? "" : prev.num_rec }));
  const handleNumRec = (e) => setForm((prev) => ({ ...prev, num_rec: e.target.value, num_fac: e.target.value ? "" : prev.num_fac }));

  // Autocompletado de NIT: reemplaza buscar.php + buscar_autorizacion.php + el typeahead
  // client-side del legacy por una búsqueda debounced contra el backend (por NIT o por
  // nombre), mostrando un desplegable de coincidencias para elegir — no solo la primera
  // que encuentre.
  useEffect(() => {
    const q = nitInput.trim();
    if (q.length < 3) { setOpcionesNit([]); return; }
    setBuscandoNit(true);
    const timer = setTimeout(() => {
      axios.get("/api/caja-chica/proveedores", { params: { buscar: q } })
        .then(({ data }) => setOpcionesNit(data?.data || []))
        .catch(() => setOpcionesNit([]))
        .finally(() => setBuscandoNit(false));
    }, 300);
    return () => { clearTimeout(timer); setBuscandoNit(false); };
  }, [nitInput]);

  const handleSeleccionProveedor = (_, opcion) => {
    if (!opcion) return;
    const proveedor = typeof opcion === "string" ? { nit: opcion } : opcion;
    setForm((prev) => ({
      ...prev,
      nit: proveedor.nit || "",
      denominacion: proveedor.denominacion || prev.denominacion,
      nro_autorizacion: proveedor.nro_autorizacion || prev.nro_autorizacion,
    }));
    setNitInput(proveedor.nit || "");
  };

  const handleClear = () => {
    setForm(initialForm);
    setError("");
    setNitInput("");
    setOpcionesNit([]);
  };

  const handleGuardar = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.post("/api/caja-chica/egresos", {
        fecha: form.fecha,
        item: form.item,
        detalle: form.detalle.trim(),
        num_fac: form.num_fac.trim(),
        num_rec: form.num_rec.trim(),
        monto: Number(form.monto),
        servicio: form.servicio,
        tipo_retencion: form.conRetencion ? form.tipo_retencion : "",
        factura: form.num_fac.trim() ? {
          fecha_factura: form.fecha_factura,
          nit: form.nit.trim(),
          denominacion: form.denominacion.trim(),
          nro_autorizacion: form.nro_autorizacion.trim(),
          cod_control: form.cod_control.trim(),
          credito_fiscal: Number(form.monto),
        } : null,
      });
      setForm(initialForm);
      setNitInput("");
      setOpcionesNit([]);
      if (pagina === 0) cargarLista(0, filasPorPagina);
      else setPagina(0); // dispara la recarga vía el useEffect, y el nuevo egreso queda visible (va primero, ORDER BY id DESC)
    } catch (e) {
      setError(e?.response?.data?.message || "Error al registrar el egreso.");
    } finally {
      setSaving(false);
    }
  };

  const puedeGuardar = form.fecha && form.item && Number(form.monto) > 0
    && (!form.conRetencion || form.tipo_retencion) && !saving;

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
        Egresos Caja Chica
      </Typography>

      {errorLista && <Alert severity="error" sx={{ mb: 1 }}>{errorLista}</Alert>}

      {loadingLista ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>
      ) : egresos.length === 0 ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>No existen Egresos.</Alert>
      ) : (
        <Paper variant="outlined" sx={{ mb: 1 }}>
          <TableContainer>
            <Table size="small" sx={{ "& td": { py: 0.15, px: 0.75, fontSize: "0.74rem", lineHeight: 1.2 } }}>
              <TableHead>
                <TableRow>
                  <Th>Cod</Th>
                  <Th>Item</Th>
                  <Th>Descripción</Th>
                  <Th align="right">Monto</Th>
                  <Th align="right">Fecha</Th>
                  <Th align="right">N° Factura</Th>
                  <Th align="right">N° Recibo</Th>
                  <Th align="center">Editar</Th>
                </TableRow>
              </TableHead>
              <TableBody>
                {egresos.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.id}</TableCell>
                    <TableCell>{r.item_descripcion}</TableCell>
                    <TableCell>{r.descripcion}</TableCell>
                    <TableCell align="right">{Number(r.monto).toFixed(2)}</TableCell>
                    <TableCell align="right">{formatFecha(r.fecha)}</TableCell>
                    <TableCell align="right">{r.num_fac || "-"}</TableCell>
                    <TableCell align="right">{r.num_recibo || "-"}</TableCell>
                    <TableCell align="center">
                      {puedeEditarFila(user, r) && (
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => setEditarId(r.id)} sx={{ color: CAJA_CHICA_COLOR, p: 0.25 }}>
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalEgresos}
            page={pagina}
            rowsPerPage={filasPorPagina}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            onPageChange={(_, nuevaPagina) => setPagina(nuevaPagina)}
            onRowsPerPageChange={(e) => { setFilasPorPagina(Number(e.target.value)); setPagina(0); }}
          />
        </Paper>
      )}

      {saldo !== null && (
        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5, color: saldo < 0 ? "error.main" : CAJA_CHICA_COLOR }}>
          Saldo {saldo < 0 ? "Negativo " : ""}{saldo.toFixed(2)}
        </Typography>
      )}

      <Divider sx={{ mb: 1.5 }} />

      <Paper variant="outlined" sx={{ p: 1.25 }}>
        {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}

        <Stack spacing={0.6}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 0.6 }}>
            <TextField
              size="small" type="date" label="Fecha de Registro" required
              InputLabelProps={{ shrink: true }}
              value={form.fecha} onChange={handleChange("fecha")}
              sx={compactFieldSx}
            />
            <TextField
              select size="small" label="Tipo Item" required
              value={form.item} onChange={handleChange("item")}
              sx={compactFieldSx}
            >
              {items.length === 0
                ? <MenuItem value="" disabled>No existen Items</MenuItem>
                : items.map((it) => <MenuItem key={it.id} value={it.id}>{it.descripcion}</MenuItem>)}
            </TextField>
          </Box>

          <TextField
            size="small" label="Detalle" name="detalle" autoComplete="on"
            value={form.detalle} onChange={handleChange("detalle")}
            sx={compactFieldSx}
          />

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" }, gap: 0.6 }}>
            <TextField
              size="small" label="N° de Factura" disabled={Boolean(form.num_rec)}
              value={form.num_fac} onChange={handleNumFac}
              sx={compactFieldSx}
            />
            <TextField
              size="small" label="N° Recibo" disabled={Boolean(form.num_fac)}
              value={form.num_rec} onChange={handleNumRec}
              sx={compactFieldSx}
            />
            <TextField
              size="small" type="number" label="Monto" required
              value={form.monto} onChange={handleChange("monto")}
              inputProps={{ min: 0, step: "0.01" }}
              sx={compactFieldSx}
            />
            <TextField
              select size="small" label="Servicio"
              value={form.servicio} onChange={handleChange("servicio")}
              sx={compactFieldSx}
            >
              {servicios.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Box>

          <FormControlLabel
            sx={{ alignSelf: "flex-start" }}
            control={
              <Checkbox
                checked={form.conRetencion}
                onChange={(e) => setForm((prev) => ({ ...prev, conRetencion: e.target.checked, tipo_retencion: e.target.checked ? prev.tipo_retencion : "" }))}
                sx={{ color: CAJA_CHICA_COLOR, "&.Mui-checked": { color: CAJA_CHICA_COLOR } }}
              />
            }
            label="Con retención"
          />
          {form.conRetencion && (
            <TextField
              select size="small" label="Tipo de retención" required
              value={form.tipo_retencion} onChange={handleChange("tipo_retencion")}
              sx={{ ...compactFieldSx, maxWidth: 320 }}
            >
              {retenciones.map((r) => <MenuItem key={r.id} value={r.nombre}>{r.nombre}</MenuItem>)}
            </TextField>
          )}

          {form.num_fac && (
            <Paper variant="outlined" sx={{ p: 1, bgcolor: "action.hover", borderLeft: "3px solid", borderLeftColor: CAJA_CHICA_COLOR }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.6, color: CAJA_CHICA_COLOR }}>
                Datos para la facturación
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 2fr" }, gap: 0.6, mb: 0.6 }}>
                <TextField
                  size="small" type="date" label="Fecha de Factura"
                  InputLabelProps={{ shrink: true }}
                  value={form.fecha_factura} onChange={handleChange("fecha_factura")}
                  sx={compactFieldSx}
                />
                <Autocomplete
                  freeSolo
                  size="small"
                  sx={compactFieldSx}
                  options={opcionesNit}
                  loading={buscandoNit}
                  inputValue={nitInput}
                  filterOptions={(x) => x}
                  onInputChange={(_, value, reason) => {
                    // Al elegir una opción, MUI dispara este evento con el label completo
                    // ("nit - denominación") para actualizar el texto mostrado — se ignora
                    // ese caso (reason !== "input"): handleSeleccionProveedor (onChange) ya
                    // dejó nitInput/form.nit con el NIT solo, sin pisarlo con el label.
                    if (reason === "input") {
                      setNitInput(value);
                      setForm((prev) => ({ ...prev, nit: value }));
                    }
                  }}
                  onChange={handleSeleccionProveedor}
                  getOptionLabel={(opcion) => (typeof opcion === "string" ? opcion : `${opcion.nit} - ${opcion.denominacion}`)}
                  isOptionEqualToValue={(opcion, valor) => opcion.nit === (valor?.nit ?? valor)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={compactFieldSx}
                      label="NIT del proveedor"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {buscandoNit && <CircularProgress size={14} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <TextField
                  size="small" label="Nombre o denominación"
                  value={form.denominacion} onChange={handleChange("denominacion")}
                  sx={compactFieldSx}
                />
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 0.6 }}>
                <TextField
                  size="small" label="Importe sujeto a crédito fiscal"
                  value={form.monto} disabled
                  sx={compactFieldSx}
                />
                <TextField
                  size="small" label="N° de Autorización"
                  value={form.nro_autorizacion} onChange={handleChange("nro_autorizacion")}
                  sx={compactFieldSx}
                />
                <TextField
                  size="small" label="Código de Control"
                  value={form.cod_control} onChange={handleChange("cod_control")}
                  sx={compactFieldSx}
                />
              </Box>
            </Paper>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={handleClear} disabled={saving}>Borrar</Button>
            <Button
              variant="contained" onClick={handleGuardar} disabled={!puedeGuardar}
              sx={{ bgcolor: CAJA_CHICA_COLOR, "&:hover": { bgcolor: "#1b5e20" } }}
            >
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <EditarEgresoDialog
        open={Boolean(editarId)}
        egresoId={editarId}
        onClose={() => setEditarId(null)}
        onEditado={() => { setEditarId(null); cargarLista(); }}
      />
    </Box>
  );
};

export default RegistroEgresosTab;
